/**
 * Theme Context
 * Manages light/dark mode state across the application
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleTheme, setTheme } from '../redux/actions/themeActions';

/**
 * Create Theme Context
 */
const ThemeContext = createContext();

/**
 * Custom hook to use Theme Context
 * @returns {Object} { mode, toggleTheme, setTheme }
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider Component
 * Wraps the app and provides theme context
 */
export const ThemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const mode = useSelector((state) => state.theme?.mode || 'light');

  const handleToggleTheme = useCallback(() => {
    dispatch(toggleTheme());
  }, [dispatch]);

  const handleSetTheme = useCallback((newMode) => {
    dispatch(setTheme(newMode));
  }, [dispatch]);

  const value = {
    mode,
    toggleTheme: handleToggleTheme,
    setTheme: handleSetTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
